import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react"
import CheckoutSuccessPopup from "../services/CheckoutSuccessPopup"
import currencyCodesData from "../services/manage/currencyCodesData.json"
import PublicPayPalCardFields from "./PublicPayPalCardFields"
import moment from "moment"
import useManageServices, {
  ServiceType,
} from "../services/hooks/useManageServices"
import { MeetingRequests } from "../../api/app.service"
import { ServiceRequests } from "../../api/app.service"
import { useAuthenticatedUser } from "../../hooks/useAuthenticatedUser"
import { UserCtx } from "../../context/userContext"
import { useToast } from "../../hooks/useToast"

interface Props {
  meetingDetails?: {
    startTime: Date
    endTime: Date
  }
  appliedDiscount: {
    amount: number
    promoCode: string
  }
  isTermsAgreed: boolean
  setIsTermsAgreed: React.Dispatch<React.SetStateAction<boolean>>
  service: any
  isGroupService: boolean
  availabilities?: any
  activeContact: any
  activeService: any
  paymentMethods: any
  selectedPaymentMethod: any
  setSelectedPaymentMethod: any
  merchantId: any
  checkoutSuccessPopup: any
  setCheckoutSuccessPopup: any
  contact: any
  activeContactRef: any
  selectedPlanFromChild?: any
  validatePromoCode: any
  createMeeting?: any
  discountInstallment: any
  discountedUpFrontAmount: any
  discountedOrderTotal: any
  promoTotal: any
  readyForPayment?: boolean
  setGuestLoading?: any
  setLoading: boolean
  validatingForm?: boolean
  cardFieldErrors: {
    cardName: boolean
    cardNumber: boolean
    cardExpiry: boolean
    cardCvv: boolean
  }
  setCardFieldErrors: React.Dispatch<
    React.SetStateAction<{
      cardName: boolean
      cardNumber: boolean
      cardExpiry: boolean
      cardCvv: boolean
    }>
  >
}

export const PublicPayPalComponent: React.FC<Props> = ({
  meetingDetails,
  appliedDiscount,
  isTermsAgreed,
  setIsTermsAgreed,
  availabilities,
  activeContact,
  activeService,
  selectedPaymentMethod,
  merchantId,
  selectedPlanFromChild,
  validatePromoCode,
  createMeeting,
  discountInstallment,
  discountedUpFrontAmount,
  discountedOrderTotal,
  promoTotal,
  readyForPayment,
  setGuestLoading,
  setLoading,
  validatingForm,
}) => {
  const clientID = process.env.REACT_APP_PAYPAL_CLIENT_ID
  const { user } = useContext(UserCtx)
  const { getServiceObject } = useManageServices()
  const { toast } = useToast()
  const [isSdkReady, setIsSdkReady] = useState(false)
  const [isChecked, setIsChecked] = useState(false)
  const [hasMounted, setHasMounted] = useState(false)
  const [sdkLoaded, setSdkLoaded] = useState(false)
  const [useSavedCard, setUseSavedCard] = useState(false)
  const [checkoutSuccessPopup, setCheckoutSuccessPopup] = useState(false)
  const [spotsLeft, setSpotsLeft] = useState<number | null>(null)
  const [activeDate, setActiveDate] = useState<Date>(
    moment(new Date()).startOf("day").tz(user?.timezone?.value)?.toDate()
  )
  const [meetings, setMeetings] = useState<any[]>([])
  const isSubx = false
  const isAuthed = useAuthenticatedUser(user)

  const determineServiceType = (service: any): ServiceType => {
    if (activeService?.service_details?.type === "recurring") return "recurring"
    if (activeService?.service_details?.type === "group") return "individual"
    if (activeService?.service_details?.type === "individual")
      return "individual"
    if (activeService?.service_details?.type === "bundle") return "bundle"
    return "consultation"
  }
  const [serviceType, setServiceType] = useState<ServiceType>(
    determineServiceType(activeService)
  )
  const [serviceValues, setServiceValues] = useState<any>({
    availability_rule_id: availabilities
      ? availabilities?.find((rule: any) => rule?.is_default).id
      : "",
    isGroupService: activeService?.is_group_service,
    color: activeService?.service_details?.image?.color,
    title: activeService?.service_details?.title,
    description: "",
    feature1: activeService?.service_details?.details?.features?.feature1,
    feature2: activeService?.service_details?.details?.features?.feature2,
    feature3: activeService?.service_details?.details?.features?.feature3,
    sessionLength: activeService?.service_details?.session?.duration,
    sessionCost: activeService?.service_details?.billing.amount,
    currency: activeService?.service_details?.symbol,
    symbol: activeService?.service_details?.symbol,
    isFree: activeService?.service_details?.isFree,
    allowPromo: activeService?.service_details?.allowPromo,
    package: {
      packageSessions: activeService?.service_details?.session?.count,
      packageBillingType:
        activeService?.service_details?.billing?.packageBillingType !==
        undefined
          ? activeService?.service_details?.billing?.packageBillingType
          : activeService?.service_details?.billing?.unit,
      packageCost: activeService?.service_details?.billing.amount,
      installmentIncrements:
        activeService?.service_details?.billing.installmentIncrements,
      upFrontPercentage:
        activeService?.service_details?.billing?.upFrontPercentage,
    },
    recurring: {
      periodCost: activeService?.service_details?.billing.amount,
      recurringFrequency: activeService?.service_details?.billing?.unit,
      sessionsPerPeriod: activeService?.service_details?.session?.count,
      unlimited: false,
    },
    expiration: {
      expireTime: activeService?.service_details?.expiration.time,
      expireType: activeService?.service_details?.expiration.type,
      expires: activeService?.service_details?.expiration?.expires,
    },
    enabled: activeService?.service_details?.enabled,
    profileServiceVisible:
      activeService?.service_details?.visibility?.profile.service,
    profileCostVisible:
      activeService?.service_details?.visibility?.profile.cost,
    marketplaceServiceVisible:
      activeService?.service_details?.visibility?.marketplace.service,
    marketplaceCostVisible:
      activeService?.service_details?.visibility?.marketplace.service,
    image: {
      exists: activeService?.service_details?.image.exists,
      base64: "",
      base64extension: "",
    },
    group: {
      isFree: activeService?.service_details?.isFree,
      weeklyDayTimes: activeService?.service_details?.group?.weeklyDayTimes,
      maximumClients: activeService?.service_details?.maximumClients,
      groupChat: activeService?.service_details?.groupChat,
    },
    serviceLinkedForms: activeService?.service_details?.serviceLinkedForms,
  })

  const user_id = parseInt(user?.user_id)
  const isCheckedRef = useRef(false)
  const activeServiceRef = useRef(activeService)
  const useSavedCardRef = useRef(useSavedCard)
  const appliedDiscountRef = useRef(appliedDiscount)
  const activeContactRef = useRef<any>(null)

  const promoTotalRef = useRef<number>(promoTotal)
  const discountedOrderTotalRef = useRef<number>(discountedOrderTotal)
  const discountedUpFrontAmountRef = useRef<number>(discountedUpFrontAmount)
  const discountInstallmentRef = useRef<number>(discountInstallment)

  useEffect(() => {
    promoTotalRef.current = promoTotal
    discountedOrderTotalRef.current = discountedOrderTotal
    discountedUpFrontAmountRef.current = discountedUpFrontAmount
    discountInstallmentRef.current = discountInstallment
  }, [
    promoTotal,
    discountedOrderTotal,
    discountedUpFrontAmount,
    discountInstallment,
  ])

  useEffect(() => {
    activeServiceRef.current = activeService
    setServiceType(activeServiceRef.current.service_details.type)
  }, [activeService])

  const userRef = useRef<typeof user | null>(null)

  useEffect(() => {
    userRef.current = user
  }, [user])

  useEffect(() => {
    activeContactRef.current = activeContact
  }, [activeContact])

  useEffect(() => {
    appliedDiscountRef.current = appliedDiscount
  }, [appliedDiscount])

  useEffect(() => {
    if (window.paypal) {
      setSdkLoaded(true)
    } else {
      console.log("sdk not loading")
    }
  }, [])

  const serviceValuesRef = useRef(serviceValues)

  useEffect(() => {
    serviceValuesRef.current = serviceValues
  }, [serviceValues])

  useEffect(() => {
    useSavedCardRef.current = useSavedCard
  }, [useSavedCard])

  useEffect(() => {
    if (activeService?.service_details?.billing?.amount) {
      const rawAmount = String(activeService.service_details.billing.amount)
      const sanitizedAmount = rawAmount.replace(/[^0-9.]/g, "")
      const parsedAmount = parseFloat(sanitizedAmount)

      if (!isNaN(parsedAmount)) {
        let calculatedPrice = parsedAmount

        if (appliedDiscountRef?.current?.amount) {
          const discount = appliedDiscountRef.current.amount

          if (!isNaN(discount)) {
            calculatedPrice = parsedAmount - discount
          }
        }

        setServiceValues((prevValues: any) => ({
          ...prevValues,
          sessionCost: calculatedPrice,
        }))
      } else {
        console.error("Invalid amount:", rawAmount)
      }
    }
  }, [activeService, appliedDiscountRef, validatePromoCode])

  useEffect(() => {
    if (activeService?.service_details) {
      setServiceValues((prev: any) => ({
        ...prev,
        symbol: activeService.service_details.symbol,
        currency: activeService.service_details.currency,
      }))
    }
  }, [activeService])

  const getGroupServiceEnrollment = async () => {
    try {
      const response = await ServiceRequests.checkGroupServiceEnrollment({
        stripe_product_id: activeService?.stripe_product_id,
      })
      const data = response
      const spotsLeft = data.spots_left
      setSpotsLeft(spotsLeft)
      return spotsLeft
    } catch (error) {
      console.error("Error fetching group service enrollment:", error)
      return null
    }
  }

  const getMeetings = () => {
    if (isAuthed) {
      const startOfActiveDate = moment(activeDate)
        .startOf("day")
        .utc()
        .tz(user?.timezone?.value)
        .toISOString()
      const endOfSixMonths = moment(activeDate)
        .add(6, "month")
        .endOf("day")
        .utc()
        .tz(user.timezone.value)
        .toISOString()

      MeetingRequests.getMeetings({
        user_id: parseInt(user.user_id),
        profile_id: user.activeProfileId,
        from_date: startOfActiveDate,
        to_date: endOfSixMonths,
      })
        .then((data) => {
          if (data && Array.isArray(data.meetings)) {
            setMeetings(data.meetings)
          } else {
            console.error("Invalid meetings data format", data)
            setMeetings([])
          }
        })
        .catch((ex) => {
          toast.error(ex.response?.data?.message || "An error occurred")
        })
    }
  }

  useEffect(() => {
    getMeetings()
  }, [])

  const createOrder = useCallback(
    async (data: any, source: "paypal" | "card") => {
      if (activeService?.service_details?.isGroupService) {
        const matchingMeeting = meetings.find(
          (meeting) =>
            meeting.service_details?.title ===
            activeService?.service_details.title
        )

        if (matchingMeeting) {
          alert("You are already enrolled in this service.")
          window.location.href = `${process.env.REACT_APP_BASE_URL}/providers/${activeContact?.endpoint_slug}/services`
          return
        }

        const spotsLeft = await getGroupServiceEnrollment()
        if (spotsLeft !== null && spotsLeft <= 0) {
          alert(
            "This group service is full. Please choose a different service."
          )
          window.location.href = `${process.env.REACT_APP_BASE_URL}/providers/${activeContact?.endpoint_slug}/services`
          return
        }
      }

      const serviceObject = getServiceObject(
        serviceType,
        serviceValuesRef.current
      )

      const service = activeServiceRef.current
      const contact = activeContactRef.current
      const saveCard = isCheckedRef.current
      const useSavedCard = useSavedCardRef.current
      const promo = appliedDiscountRef.current.promoCode
      const user = userRef.current

      return ServiceRequests.createPurchaseService({
        user_id: user_id || user?.user_id || 0,
        coach_user_id: contact?.user_id,
        client_profile_id: user?.activeProfileId,
        coach_profile_id: contact?.profile_id,
        payment_method_id: useSavedCard ? selectedPaymentMethod?.id : "",
        service: { ...serviceObject },
        stripe_product_id: service.stripe_product_id,
        source: source,
        save_for_future: saveCard,
        user_environment: "",
        applied_discounts: {
          promo_value: promoTotalRef.current, // total value of promo
          order_total: discountedOrderTotalRef.current, // total value of ORDER TOTAL after promocode if applied || recurring total values = each recurring payment
          order_total_upfront: discountedUpFrontAmountRef.current, // total value of TOTAL upfront payment with applicable promo code (for installment)
          order_total_installment: discountInstallmentRef.current, // total value of TOTAL installment payments with applicable promo code (for installment)
        },
      })
        .then((response: any) => {
          return response.data
        })
        .then((order: any) => {
          if (order.id) {
            return order.id
          } else if (order.data?.status === "COMPLETED") {
            return order.id
          }
        })
    },
    [useSavedCard, selectedPaymentMethod]
  )

  // Finalize the transaction after payer approval
  const onApprove = useCallback(
    async (data: any, source: "paypal" | "card") => {
      const service = activeServiceRef.current
      const getContact = activeContactRef.current
      const promo = appliedDiscountRef.current.promoCode
      const user = userRef.current

      try {
        const response = await ServiceRequests.completePurchaseService({
          user_id: Number(user_id || user?.user_id || 0),
          coach_user_id: getContact?.user_id,
          client_profile_id: user?.activeProfileId,
          coach_profile_id: getContact?.profile_id,
          payment_method_id: useSavedCard ? selectedPaymentMethod?.id : "",
          stripe_product_id: service.stripe_product_id,
          source,
          order: data.orderID || data,
          user_environment: "",
          service_payment_id: "",
          applied_discounts: {
            entered_promo_code: promo,
            promo_value: promoTotalRef.current,
            order_total: discountedOrderTotalRef.current,
            order_total_upfront: discountedUpFrontAmountRef.current,
            order_total_installment: discountInstallmentRef.current,
          },
        })

        if (meetingDetails?.startTime && meetingDetails?.endTime) {
          await createMeeting(response?.service_usage_id)
        } else if (service?.service_details?.isGroupService) {
          await MeetingRequests.updateGroupMeeting({
            profile_id: user?.activeProfileId,
            user_id: Number(user_id || user?.user_id || 0),
            coach_user_id: getContact?.user_id,
            group_service_id: service.group_service_id,
          })
        }
        localStorage.removeItem("meetingToSchedule")
        localStorage.removeItem("coachRedirect")
        localStorage.removeItem("memberRedirect")
        setGuestLoading(false)
        setCheckoutSuccessPopup(true)
      } catch (error: any) {
        setGuestLoading(false)
        console.error("Error in onApprove:", error)
        toast.error(error?.response?.data?.message)
      }
    },
    [useSavedCard, selectedPaymentMethod]
  )

  useEffect(() => {
    isCheckedRef.current = isChecked
  }, [isChecked])

  useEffect(() => {
    if (clientID && merchantId) {
      let currency = serviceValues.currency

      const match = currencyCodesData.codes.find(
        (item) =>
          item.code.toUpperCase() === currency.toUpperCase() ||
          item.symbol === currency
      )

      if (match) {
        currency = match.code
      }

      const loadPayPalSDK = () => {
        if (document.getElementById("paypal-sdk")) {
          console.log("PayPal SDK already loaded.")
          setIsSdkReady(true)
          return
        }

        const script = document.createElement("script")
        script.id = "paypal-sdk"
        script.src = `https://www.paypal.com/sdk/js?components=buttons,card-fields&client-id=${clientID}&merchant-id=${merchantId}&currency=${currency}&intent=capture&disable-funding=paylater`
        script.async = true

        script.onload = () => {
          console.log("PayPal SDK loaded successfully.")
          setIsSdkReady(true)
        }

        script.onerror = (err) => {
          console.error("Failed to load PayPal SDK:", err)
        }

        document.body.appendChild(script)
      }

      loadPayPalSDK()
    }
  }, [clientID, merchantId, serviceValues.currency])

  useEffect(() => {
    if (isSdkReady) {
      setHasMounted(true)
    }
  }, [isSdkReady])

  if (!isSdkReady || !hasMounted) {
    return <div>Loading PayPal SDK...</div>
  }

  return (
    <>
      {checkoutSuccessPopup && (
        <CheckoutSuccessPopup
          checkoutSuccessPopup={checkoutSuccessPopup}
          setCheckoutSuccessPopup={setCheckoutSuccessPopup}
          contact={activeContact}
          meetingDetails={meetingDetails}
          activeContact={activeContact}
          activeService={activeService}
        />
      )}
      {discountedOrderTotal !== 0 && (
        <div className="justify-flex flex-col items-center justify-center">
          <PublicPayPalCardFields
            isCheckedRef={isCheckedRef}
            isChecked={isChecked}
            setIsChecked={setIsChecked}
            onApprove={onApprove}
            createOrder={createOrder}
            isTermsAgreed={isTermsAgreed}
            setIsTermsAgreed={setIsTermsAgreed}
            redirectUrl=""
            selectedPlanFromChild={selectedPlanFromChild}
            isSubx={isSubx}
            activeService={activeService}
            isSdkReady={isSdkReady}
            readyForPayment={readyForPayment}
            validatingForm={validatingForm}
            setLoading={setLoading}
          />
        </div>
      )}
    </>
  )
}

export default PublicPayPalComponent
